/**
 * Router hooks for `<ClerkProvider routerPush routerReplace>`.
 *
 * Without these, clerk-js falls back to `window.location` for EVERY
 * navigation its prebuilt components make — including the steps inside
 * `<SignIn>` (`/sign-in` → `/sign-in/factor-one` → `/sign-in/factor-two`).
 * That full reload is bad twice over: the app re-boots (splash) between the
 * email and password forms, and Clerk's internal router `flushSync`-renders
 * the next step in the old page right after kicking off the reload, so the
 * second-factor / new-device code card mounts (and sends its email) once
 * before unload and once again on the fresh boot — two verification emails
 * per password sign-in.
 *
 * Clerk tags each navigation with `__internal_metadata.navigationType`:
 *
 * - `'internal'`: within a component's `path` (sign-in steps). Routed through
 *   the SPA router, so no reload and a single mount per step.
 * - `'external'` / `'window'` / absent: leaving the component (post-sign-in
 *   redirect to `fallbackRedirectUrl`, `afterSignOutUrl`, sign-in ⇄ sign-up
 *   links). Deliberately kept as Clerk's own window navigation: the root
 *   route guard reads `context.auth` from the last React render, and a
 *   client-side navigation fired synchronously after `setActive` could run
 *   `beforeLoad` against a stale signed-out context and bounce back to
 *   `/sign-in`. A reload boots with the fresh session cookie instead.
 */

export type ClerkNavigationType = 'internal' | 'external' | 'window';

export interface ClerkRouterMetadata {
    __internal_metadata?: { navigationType?: ClerkNavigationType };
    windowNavigate: (to: URL | string) => void;
}

export type ClerkRouterFn = (to: string, metadata?: ClerkRouterMetadata) => Promise<unknown> | unknown;

export interface SpaNavigateOptions {
    href: string;
    replace: boolean;
}

/**
 * Builds the `routerPush` / `routerReplace` pair from an SPA navigate
 * function (the TanStack router's `navigate({ href, replace })`).
 */
export function createClerkRouterFns(
    spaNavigate: (opts: SpaNavigateOptions) => Promise<unknown> | unknown,
): { routerPush: ClerkRouterFn; routerReplace: ClerkRouterFn } {
    const make =
        (replace: boolean): ClerkRouterFn =>
        (to, metadata) => {
            if (metadata?.__internal_metadata?.navigationType === 'internal') {
                return spaNavigate({ href: to, replace });
            }
            if (metadata?.windowNavigate) {
                metadata.windowNavigate(to);
                return;
            }
            window.location.assign(to);
        };
    return { routerPush: make(false), routerReplace: make(true) };
}
